import { attrsToBuilderProps } from '@/components/tiptap-editor/tiptap-extension/short-answer-extension/tool-settings/lib/assessment-attrs';
import { escapeXml } from '@/lib/utils';

const EXPORT_ASSESSMENT_KEYS = new Set([
    'ignoreend',
    'tolerance',
    'width',
    'absolute_value',
    'require_sig_fig',
    'require_n_or_fewer_sig_figs',
    'placeholder',
    'accept_E_notation',
    'accept_scientific_notation',
    'hide_E_notation',
    'give_units',
    'physical_quantities',
    'preferred_units',
    'require_engineering_notation',
    'require_strict_scientific_notation',
    'require_units',
    'unit_prefixes',
    'unitless_option',
    'boolean_functions',
    'boolean_variables',
    'correctness_methods',
    'editor_atomic_symbols',
    'operators',
    'support_true_and_false',
    'untyped_variables',
    'accepted_substitute_variables',
    'expected_functions',
    'expected_variables',
    'tests',
    'virtual_keyboard_layout_name',
    'functions_variables',
    'language',
]);

/** Matches zycommon `symbolicPropertyToData` JSON-typed fields (stored as JSON strings in zyBooks payloads). */
const JSON_STRING_EXPORT_KEYS = new Set([
    'editor_atomic_symbols',
    'accepted_substitute_variables',
    'expected_functions',
    'expected_variables',
    'tests',
]);

/**
 * Ember `fromJSON` uses comma-split for `Array<string>` assessment fields.
 * If the editor ever holds arrays, join them for export.
 */
const COMMA_JOIN_EXPORT_KEYS = new Set([
    'physical_quantities',
    'preferred_units',
    'unit_prefixes',
    'boolean_functions',
    'boolean_variables',
    'correctness_methods',
    'operators',
    'untyped_variables',
]);

function serializeJsonStringAssessmentValue(value: unknown): string | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }
    let jsonString: string;
    if (typeof value === 'string') {
        jsonString = value;
    } else {
        try {
            jsonString = JSON.stringify(value);
        } catch {
            jsonString = String(value);
        }
    }
    // Legacy zyBooks payloads store JSON-typed fields with XML/HTML entities (see common-decimal-symbolic-short-answer-question fromJSON).
    return escapeXml(jsonString);
}

function serializeAssessmentValueForZybooks(key: string, value: unknown): unknown {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (JSON_STRING_EXPORT_KEYS.has(key)) {
        return serializeJsonStringAssessmentValue(value);
    }
    if (COMMA_JOIN_EXPORT_KEYS.has(key) && Array.isArray(value)) {
        return value.map(String).join(',');
    }
    return value;
}

export function buildExportAssessmentFields(attrs: Record<string, unknown>): Record<string, unknown> {
    const merged = attrsToBuilderProps(attrs) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    if (merged.assessment_type != null && merged.assessment_type !== 'string') {
        out.assessment_type = merged.assessment_type;
    }
    if (merged.notation != null && merged.notation !== '') {
        out.notation = merged.notation;
    }

    for (const key of EXPORT_ASSESSMENT_KEYS) {
        if (!(key in merged)) {
            continue;
        }
        const v = merged[key];
        if (v === undefined) {
            continue;
        }
        if (typeof v === 'string' && v === '' && key !== 'ignoreend' && key !== 'placeholder') {
            continue;
        }
        const serialized = serializeAssessmentValueForZybooks(key, v);
        if (serialized !== undefined) {
            out[key] = serialized;
        }
    }

    return out;
}
